/*
 * Wrap a page in the site theme: provider + palette + the Alignment page type.
 *
 * A page moved onto theme tokens renders its content inside <ThemedPage>. The
 * rounded panel is what carries the palette's background, so a light page
 * reads as a sheet sitting in the (still dark) app chrome.
 */
import React from "react";
import { ThemeProvider, useTheme } from "./ThemeProvider";
import { BODY } from "../alignmentUi";
import { PaletteContext } from "./legible";

function Panel({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const { themeClass, theme } = useTheme();
  return (
    <PaletteContext.Provider value={theme}>
      <div
        className={`${themeClass} rounded-3xl bg-[var(--c-bg)] p-4 sm:p-6 lg:p-8 antialiased ${className}`}
        style={BODY}
      >
        {children}
      </div>
    </PaletteContext.Provider>
  );
}

export function ThemedPage({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <ThemeProvider>
      <Panel className={className}>{children}</Panel>
    </ThemeProvider>
  );
}

/* Light / dark switch. The choice is site-wide (see ThemeProvider). */
export function ThemeToggle({ compact = false }: { compact?: boolean }) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  return (
    <button
      type="button"
      onClick={toggleTheme}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className={
        compact
          // Sized to sit in the nav pill beside the tab buttons.
          ? "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[var(--c-ink-3)] transition hover:bg-[var(--c-surface)] hover:text-[var(--c-ink)]"
          : "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] text-[var(--c-ink-2)] shadow-[var(--c-shadow-sm)] transition hover:border-[var(--c-line-strong)] hover:text-[var(--c-ink)]"
      }
    >
      {isDark ? (
        <svg className={compact ? "h-4 w-4" : "h-5 w-5"} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
      ) : (
        <svg className={compact ? "h-4 w-4" : "h-5 w-5"} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
      )}
    </button>
  );
}

/*
 * A span whose text is a runtime color (WoW class, parse, role). The color is
 * darkened just enough to read on a light palette and left alone on dark. For
 * code that sits outside the provider and so cannot call useTheme itself.
 */
export function LegibleSpan({
  color,
  style,
  ...rest
}: React.HTMLAttributes<HTMLSpanElement> & { color: string }) {
  const { onLight } = useTheme();
  return <span {...rest} style={{ ...style, color: onLight(color) }} />;
}

/*
 * Apply the palette to an app-shell region (header, background) that is not a
 * page. `themed` false pins the region to dark: used on tabs whose page has
 * not been moved onto tokens yet, so a page is never half light, half dark.
 */
export function ThemeScope({
  themed,
  as: Tag = "div",
  className = "",
  children,
}: {
  themed: boolean;
  as?: "div" | "header";
  className?: string;
  children?: React.ReactNode;
}) {
  const { themeClass, theme } = useTheme();
  return (
    <PaletteContext.Provider value={themed ? theme : "dark"}>
      <Tag className={`${themed ? themeClass : "lfm-theme lfm-dark"} ${className}`}>{children}</Tag>
    </PaletteContext.Provider>
  );
}
